import { useCallback } from 'react';
import { useFile, useSelection, useVisualizer, useUI } from '../context/AppContext.jsx';
import { convertWaypointsToWorldCoords } from '../utils/coordinateTransforms.js';
import { extractNodes } from '../utils/nodeUtils.js';
import {
  mirrorWaypoint,
  getWaypointIndicesToMirror,
  groupWaypointsByParentNode
} from '../services/MirrorService.js';

export function useMirror() {
  const {
    parser,
    currentWaypoints,
    setCurrentWaypoints,
    nodes,
    setNodes,
    currentFeatures,
  } = useFile();
  const { multiSelectedIndices } = useSelection();
  const { visualizer } = useVisualizer();
  const { showStatus, setIsLoading, setLoadingProgress, setLoadingText } = useUI();

  const applyMirror = useCallback(async (scope, nodeIndex, featureName, plane, createNew) => {
    try {
      // Find the mirror feature
      const mirrorFeature = currentFeatures.find(f => f.name === featureName);
      if (!mirrorFeature) {
        showStatus('Mirror feature not found', 'error');
        return;
      }

      // Use service to determine which waypoints to mirror
      const { indices, error } = getWaypointIndicesToMirror(
        scope,
        currentWaypoints,
        multiSelectedIndices,
        nodes,
        nodeIndex
      );
      
      if (error) {
        showStatus(error, 'error');
        return;
      }

      // Track new waypoints if creating new ones
      const newWaypointIndices = [];

      const showProgress = indices.length > 50;
      if (showProgress) {
        setIsLoading(true);
        setLoadingText('Mirroring waypoints...');
        setLoadingProgress(0);
      }

      let updatedCount = 0;
      const totalItems = indices.length;
      let updatedWaypoints = [...currentWaypoints];

      if (createNew) {
        // When creating new waypoints, group by parent node to maintain structure
        const waypointUpdates = new Map(); // waypoint index -> {position, rotation, viaPoint}

        // First pass: calculate all transformations using service
        for (let i = 0; i < indices.length; i++) {
          const index = indices[i];
          const waypoint = currentWaypoints[index];

          // Find the waypoint's feature
          const waypointFeature = currentFeatures.find(f => f.name === waypoint.feature);
          if (!waypointFeature) {
            console.warn(`Feature ${waypoint.feature} not found for waypoint ${index}`);
            continue;
          }

          // Use service to calculate mirrored coordinates
          const mirrored = mirrorWaypoint(waypoint, waypointFeature, mirrorFeature, plane);

          // Store the update for this waypoint
          waypointUpdates.set(index, mirrored);
        }
        
        // Group waypoints by parent node using service
        const parentNodeGroups = groupWaypointsByParentNode(currentWaypoints, indices);

        // Second pass: clone each unique parent node once
        for (const [parentNode, waypointIndices] of parentNodeGroups) {
          const newIndices = parser.cloneParentNode(parentNode, waypointUpdates);
          newWaypointIndices.push(...newIndices);
          updatedCount += waypointIndices.length;
        }
      } else {
        // Update existing waypoints
        for (let i = 0; i < indices.length; i++) {
          const index = indices[i];
          const waypoint = currentWaypoints[index];

          // Find the waypoint's feature
          const waypointFeature = currentFeatures.find(f => f.name === waypoint.feature);
          if (!waypointFeature) {
            console.warn(`Feature ${waypoint.feature} not found for waypoint ${index}`);
            continue;
          }

          // Use service to calculate mirrored coordinates
          const mirrored = mirrorWaypoint(waypoint, waypointFeature, mirrorFeature, plane);

          // Update existing waypoint
          parser.updateWaypointPosition(index, mirrored.position);
          parser.updateWaypointRotation(index, mirrored.rotation);

          updatedWaypoints[index] = {
            ...updatedWaypoints[index],
            position: mirrored.position,
            rotation: mirrored.rotation,
          };

          // Update via-point if it exists
          if (waypoint.viaPoint && mirrored.viaPoint) {
            parser.updateViaPointPosition(index, mirrored.viaPoint.position);
            parser.updateViaPointRotation(index, mirrored.viaPoint.rotation);
            updatedWaypoints[index].viaPoint = mirrored.viaPoint;
          }

          updatedCount++;

          // Update progress for large operations
          if (showProgress && i % 10 === 0) {
            const progress = ((i + 1) / totalItems) * 100;
            setLoadingProgress(progress);
            setLoadingText(`Mirrored ${i + 1} of ${totalItems} waypoints`);
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      }

      // If we created new waypoints, re-extract all waypoints from the parser
      if (createNew && newWaypointIndices.length > 0) {
        updatedWaypoints = parser.extractWaypoints();
        // Re-extract nodes to update the tree structure
        const extractedNodes = extractNodes(updatedWaypoints);
        setNodes(extractedNodes);
      }

      // Update all waypoints at once
      setCurrentWaypoints(updatedWaypoints);

      // Hide progress overlay
      if (showProgress) {
        setIsLoading(false);
      }

      // Re-visualize all waypoints
      if (visualizer) {
        const waypointsForVis = convertWaypointsToWorldCoords(updatedWaypoints, currentFeatures);
        visualizer.visualizeWaypoints(waypointsForVis);
      }

      if (createNew) {
        showStatus(`Created ${updatedCount} new mirrored waypoint(s)`, 'success');
      } else {
        showStatus(`Mirrored ${updatedCount} waypoint(s)`, 'success');
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Mirror error:', error);
      showStatus(`Error: ${error.message}`, 'error');
    }
  }, [parser, visualizer, currentWaypoints, setCurrentWaypoints, multiSelectedIndices, nodes, setNodes, currentFeatures, showStatus, setIsLoading, setLoadingProgress, setLoadingText]);

  return { applyMirror };
}
